import { clear, setItem } from '@/utils/localStorage';
import { removeCookies } from '@/utils/cookies';
import axios, { AxiosRequestConfig, Canceler } from 'axios';
import { useStore } from '@/helper/pinia-auto-refs';
import jsonBig from 'json-bigint';
import { generateNonce, generateTimestamp, generateSign } from '@/utils/sign';

axios.defaults.timeout = 120000;
axios.defaults.baseURL = '/flagos/api/v1';
/**
 * 响应数据转换器：处理大数字精度丢失问题
 *
 * 问题背景：
 * - 接口返回的 user_id 等字段可能是 number 类型
 * - JavaScript 的 Number 类型只能安全表示 -2^53 到 2^53 之间的整数（即 -9007199254740991 到 9007199254740991）
 * - 超出此范围的数字在 JSON.parse() 时会发生精度丢失，例如：153096191771852830 可能被转换为 153096191771852840
 *
 * 解决方案：
 * - 使用 json-bigint 库替代原生的 JSON.parse()
 * - 配置 storeAsString: true，将超出安全整数范围的大数字自动转换为字符串，避免精度丢失
 * - 这样 user_id 等大数字字段会以字符串形式保存，保证数据准确性
 *
 * 降级处理：
 * - 如果 json-bigint 解析失败，尝试使用原生 JSON.parse() 作为降级方案
 * - 如果都失败，返回原始数据，确保不会因为解析错误导致请求失败
 */
axios.defaults.transformResponse = [
    data => {
        try {
            // 确保 data 是字符串类型，jsonBig.parse 需要字符串参数
            if (typeof data === 'string') {
                // 使用 json-bigint 解析，storeAsString: true 会将大数字转换为字符串
                return jsonBig({ storeAsString: true }).parse(data);
            }
            // 如果 data 已经是对象，直接返回（可能已经被其他 transformResponse 处理过）
            return data;
        } catch (err) {
            // 如果 json-bigint 转换失败，尝试使用原生 JSON.parse 作为降级方案
            // 这种情况通常发生在数据格式异常时
            try {
                if (typeof data === 'string') {
                    return JSON.parse(data);
                }
                return data;
            } catch (parseErr) {
                // 如果都失败了，返回原始数据，避免因解析错误导致请求失败
                return data;
            }
        }
    },
];
axios.interceptors.request.use(
    config => {
        let token = '';
        try {
            token = JSON.parse(localStorage.getItem('data-appToken') || '{}')?.accessToken;
        } catch (e) {}
        // 确保 headers 存在
        config.headers = config.headers || {};
        config.headers['Authorization'] = 'Bearer ' + token;

        const timestamp = generateTimestamp();
        const nonce = generateNonce();

        // 用于计算签名的参数（包括 body,params）
        const signParams = {
            timestamp,
            nonce,
            ...(config.data || {}),
            ...(config.params || {}),
            method: config.method,
        };

        // 处理 URL：去掉 /flagos 及其之前的所有内容
        let requestUrl = `${config.baseURL}${config.url}`;
        const flagosIndex = requestUrl.indexOf('/flagos');
        if (flagosIndex !== -1) {
            // 找到 /flagos 的位置，保留 /flagos 之后的内容
            requestUrl = requestUrl.substring(flagosIndex + '/flagos'.length);
        }
        const sign = generateSign(signParams, requestUrl);

        config.headers['x-timestamp'] = String(timestamp);
        config.headers['x-nonce'] = nonce;
        config.headers['x-signature'] = sign;

        // 从 store 中获取 userId 并设置到请求头
        try {
            const userStore = useStore('user');
        } catch (e) {
            // 如果获取失败，忽略错误，不影响请求
            console.warn('获取 userId 失败:', e);
        }

        return config;
    },
    error => {
        return Promise.reject(error.response);
    }
);
/**
 * 请求响应拦截
 * 用于处理需要在请求返回后的操作
 */
axios.interceptors.response.use(
    response => {
        const responseCode = response.status;
        const responseDataCode = response.data?.code;
        // 如果返回的状态码为200，说明接口请求成功，可以正常拿到数据
        // 否则的话抛出错误
        if (responseCode >= 200 && responseCode < 300) {
            if (
                (responseDataCode >= 200 && responseDataCode < 300) ||
                ['0', 0, '200', 200, '00'].includes(responseDataCode) ||
                response?.data instanceof Blob
            ) {
                return Promise.resolve(response);
            } else {
                return Promise.reject(response);
            }
        } else {
            return Promise.reject(response);
        }
    },
    error => {
        return Promise.reject(error);
    }
);

export async function cancelRequest(config: AxiosRequestConfig) {
    // 创建一个取消令牌
    const CancelToken = axios.CancelToken;
    let cancel: Canceler | undefined;

    const axioRequest = async () => {
        return await request({
            ...config,
            cancelToken: new CancelToken(c => (cancel = c)),
        }).finally(() => {
            cancel = undefined;
        });
    };
    const cancelAxioRequest = () => cancel && cancel('Operation canceled by the user.');
    return {
        axioRequest,
        cancelAxioRequest,
    };
}

/**
 * 从错误对象中提取错误消息
 * @param error - 错误对象
 * @returns 错误消息字符串
 */
const extractErrorMessage = (error: any): string => {
    if (error?.response?.data) {
        const data = error.response.data;
        // 尝试从不同路径获取错误消息
        return data.msg || data.message || data.detail?.msg || data.detail?.message || data.error || error.message;
    }
    return error.message || error.data?.message || '请求失败';
};

export async function request(config: AxiosRequestConfig, isJumpLogin = true) {
    try {
        const data = await axios(config);
        return data.data;
    } catch (e: any) {
        const message = extractErrorMessage(e);

        if (axios.isCancel(e)) {
            return Promise.reject(new Error('Request canceled: ' + message));
        }

        const { clearUserInfo } = useStore('user');
        if (e?.response?.status === 401) {
            clearUserInfo();
            clear();
            removeCookies(); // 清除所有 cookie

            if (isJumpLogin) {
                !location.pathname.endsWith('/login') && setItem('redirect_url', location.href);
                location.href = import.meta.env.VITE_MODE === 'production' ? '/login' : `/zhongzhi/login`;
            }
            // 401 错误也应该被抛出，而不是返回 undefined
            return Promise.reject(new Error(message || 'Unauthorized'));
        }

        // 将错误信息附加到错误对象上，方便调用方获取
        const error = new Error(message);
        return Promise.reject(error);
    }
}
